import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme, styled } from '@mui/material/styles';
import LoadingButton from '@mui/lab/LoadingButton';
import { Grid, Stack, Typography, Link, useMediaQuery, Box, FormHelperText, TextField } from '@mui/material';

// project imports
import AuthCardWrapper from 'components/Cards/AuthCardWrapper';
import logoImg from 'assets/images/raptor_tech_logo.png';
import { authTwoFactor, authLogin } from 'components/../redux/actions/auth';

const AuthWrapper = styled('div')(({ theme }) => ({
    background: `linear-gradient(white, ${theme.palette.primary.main})`
    // minHeight: '100vh'
}));

const TwoFactor = () => {
    const location = useLocation();
    const { username, password } = location.state || {};
    const { userInfo } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));

    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [resendLoading, setResendLoading] = useState(false);
    const [resendDisabled, setResendDisabled] = useState(true);
    const [countdown, setCountdown] = useState(60);

    useEffect(() => {
        if (!username && !password) {
            navigate('/login');
        }
    }, [username, password, navigate]);

    useEffect(() => {
        if (resendDisabled) {
            const timer = setInterval(() => {
                setCountdown((prevCounter) => {
                    if (prevCounter <= 1) {
                        clearInterval(timer);
                        setResendDisabled(false);
                        return 0;
                    }
                    return prevCounter - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [resendDisabled]);

    useEffect(() => {
        if (userInfo) {
            if (userInfo.admin_permissions.root_admin) {
                navigate('/management/organizations');
            } else {
                navigate('/home');
            }
        }
    }, [navigate, userInfo]);

    const formik = useFormik({
        initialValues: {
            code: '',
            submit: null
        },
        validationSchema: Yup.object({
            code: Yup.string().matches(/^\d+$/, 'Verification Code must be numeric').max(6).required('Verification Code is required')
        }),
        onSubmit: (values, helpers) => {
            console.log(values);
            setLoading(true);
            dispatch(authTwoFactor({ code: values.code, username, password }))
                .then(() => setLoading(false))
                .catch(() => setLoading(false));
        }
    });

    const handleResendCode = () => {
        setResendLoading(true);
        setResendDisabled(true);
        setCountdown(60);
        dispatch(authLogin({ username, password }))
            .then(() => {
                setResendLoading(false);
            })
            .catch(() => setResendLoading(false));
    };

    return (
        <AuthWrapper>
            <Grid container direction="column" justifyContent="flex-end" sx={{ minHeight: '100vh' }}>
                <Grid item xs={12}>
                    <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: 'calc(100vh - 68px)' }}>
                        <Grid item sx={{ m: { xs: 1, sm: 3 }, mb: 0 }}>
                            <AuthCardWrapper>
                                <Grid container spacing={2} alignItems="center" justifyContent="center">
                                    <Grid item sx={{ mb: 3 }}>
                                        <Link href="/" aria-label="WakeSMART Administration Login">
                                            <img src={logoImg} width="150" alt="" />
                                        </Link>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Grid
                                            container
                                            direction={matchDownSM ? 'column-reverse' : 'row'}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <Grid item>
                                                <Stack alignItems="center" justifyContent="center" spacing={1}>
                                                    <Typography
                                                        color={theme.palette.primary.main}
                                                        gutterBottom
                                                        variant={matchDownSM ? 'h3' : 'h2'}
                                                    >
                                                        WakeSMART Administration Login
                                                    </Typography>
                                                    <Typography
                                                        color="text.secondary"
                                                        fontWeight={200}
                                                        gutterBottom
                                                        variant={matchDownSM ? 'h5' : 'h4'}
                                                    >
                                                        Check your email for the verification code.
                                                    </Typography>
                                                </Stack>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <form noValidate onSubmit={formik.handleSubmit}>
                                            <TextField
                                                fullWidth
                                                label="Verification Code"
                                                margin="normal"
                                                name="code"
                                                onBlur={formik.handleBlur}
                                                onChange={formik.handleChange}
                                                value={formik.values.code}
                                                variant="outlined"
                                                error={Boolean(formik.touched.code && formik.errors.code)}
                                                helperText={formik.touched.code && formik.errors.code}
                                            />
                                            <Box sx={{ mt: 2, fontFamily: 'sans-serif', fontSize: 8 }}>
                                                <LoadingButton
                                                    // disabled={formik.isSubmitting}
                                                    fullWidth
                                                    size="large"
                                                    type="submit"
                                                    variant="contained"
                                                    loading={loading}
                                                >
                                                    Log In
                                                </LoadingButton>
                                            </Box>
                                            <Box sx={{ mt: 2, fontFamily: 'sans-serif', fontSize: 8 }}>
                                                <LoadingButton
                                                    disabled={resendDisabled}
                                                    fullWidth
                                                    size="large"
                                                    type="button"
                                                    variant="contained"
                                                    loading={resendLoading}
                                                    onClick={handleResendCode}
                                                >
                                                    {resendDisabled ? `Resend Code (${countdown})` : 'Resend Code'}
                                                </LoadingButton>
                                            </Box>
                                        </form>
                                    </Grid>
                                </Grid>
                            </AuthCardWrapper>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </AuthWrapper>
    );
};

export default TwoFactor;
